import { Session, WorkshopDayScheduleRow } from './sessionTypes';

export const convertTo12HourFormat = (timeStr: string): string => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const modifier = hours >= 12 ? "PM" : "AM";

  const convertedHours = hours % 12 || 12;

  return `${convertedHours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${modifier}`;
};

export const parseTime = (timeStr: string): string => {
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
};

export const categorizeSessions = (allSessions: Session[]) => {
  const now = new Date();
  const past: Session[] = [];
  const upcoming: Session[] = [];
  const ongoing: Session[] = [];

  allSessions.forEach((session) => {
    // Completed tab is driven by session_status (ended / cancelled / completed)
    const status = (session.session_status || "").trim().toLowerCase();
    const pastStatuses = new Set(["ended", "cancelled", "completed"]);
    if (pastStatuses.has(status) || session.hasEnded === true) {
      past.push(session);
      return;
    }

    // Check if session has started based on Date and Start_Time comparison
    let hasStarted = false;
    if (session.Date && session.Date.trim() !== '' && session.Start_Time && session.Start_Time.trim() !== '') {
      try {
        // Check if Start_Time is in 12-hour format (contains AM/PM)
        const timeStr = session.Start_Time.trim();
        const is12HourFormat = /AM|PM/i.test(timeStr);
        const formattedTime = is12HourFormat ? parseTime(timeStr) : timeStr;
        const sessionDateTime = new Date(`${session.Date}T${formattedTime}`);
        hasStarted = now >= sessionDateTime;
      } catch (error) {
        // If parsing fails, fall back to checking if values exist
        hasStarted = false;
      }
    }

    // live / paused (LCM Incomplete), Actual_Start_Time_date, or schedule passed → ongoing
    if (
      session.session_status === "live" ||
      status === "paused" ||
      (session.Actual_Start_Time_date && session.Actual_Start_Time_date.trim() !== '') ||
      hasStarted
    ) {
      ongoing.push(session);
      return;
    }

    // All other sessions are upcoming
    upcoming.push(session);
  });

  return { past, upcoming, ongoing };
};

export const createSessionNameSlug = (sessionName: string): string => {
  return sessionName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export const getRoutePrefix = (): string => {
  const currentPath = window.location.pathname;
  const isAdminRoute = currentPath.startsWith('/admin');
  const hasAdminPermission = sessionStorage.getItem("Admin");
  
  if (isAdminRoute && hasAdminPermission) {
    return "/admin";
  }
  return "/trainer";
};

export const getViewSessionsPath = (prefix?: string): string =>
  `${prefix || getRoutePrefix()}/view-sessions`;

export const getSessionReportPath = (
  sessionNameSlug: string,
  sessionId: number | string,
  prefix?: string,
): string =>
  `${prefix || getRoutePrefix()}/session-report/${sessionNameSlug}?sessionId=${sessionId}`;

export const getSessionAnalyticsPath = (
  sessionNameSlug: string,
  sessionId: number | string,
  prefix?: string,
): string =>
  `${prefix || getRoutePrefix()}/session-analytics/${sessionNameSlug}?sessionId=${sessionId}`;


export const getAssignSessionsPath = (prefix?: string): string =>
  `${prefix || getRoutePrefix()}/assign-sessions`;

export const getCreateSessionPath = (prefix?: string): string =>
  `${prefix || getRoutePrefix()}/create-session`;

export type AddStudentsQueryFilters = {
  sessionId: number;
  track?: string | string[];
  course?: string | string[];
  batch?: string | string[];
  subject?: string | string[];
  project?: string | string[];
};

const toCommaList = (value: string | string[] | undefined): string => {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.filter(Boolean).map(String).join(',');
  return String(value);
};

export const buildAddStudentsQueryString = (
  filters: AddStudentsQueryFilters
): string => {
  const params = new URLSearchParams();
  if (filters.sessionId > 0) {
    params.set('sessionId', String(filters.sessionId));
  }
  const track = toCommaList(filters.track);
  if (track) params.set('track', track);
  const course = toCommaList(filters.course);
  if (course) params.set('course', course);
  const batch = toCommaList(filters.batch);
  if (batch) params.set('batch', batch);
  const subject = toCommaList(filters.subject);
  if (subject) params.set('subject', subject);
  const project = toCommaList(filters.project);
  if (project) params.set('project', project);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export const getAddStudentsPath = (
  prefix: string,
  filters: AddStudentsQueryFilters
): string => `${prefix}/add-students${buildAddStudentsQueryString(filters)}`;

/** MRT layout: parent must be flex column with flex:1; only the table body scrolls. */
export const sessionTablePaperSx = {
  borderRadius: "8px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  boxShadow: "none",
  border: "1px solid #e5e7eb",
};

export const sessionTableContainerSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
};

const CATEGORY_CODE_LABEL = /^(LCM|LCO|LC|LM|LO|LW-Online|LW-Onsite|LW)$/i;

/** Batch line under the session title. Category codes (LCM, LCO, …) stay off this line. */
export const sessionNameSubtitle = (
  session: Session,
  batchLabel = "",
): string => {
  const text = String(session.batch_name || batchLabel || "").trim();
  if (!text || CATEGORY_CODE_LABEL.test(text)) return "";
  return text;
};

/** Online vs Onsite for View Sessions TYPE column. */
export const getSessionDeliveryTypeLabel = (session: Session): string => {
  const category = String(session.live_session_category || "").toLowerCase();
  if (category === "workshop_onsite") return "Onsite";
  const title = String(session.Session_Topic || "").toUpperCase();
  if (title.startsWith("LW-ONSITE") || /^LW\s/.test(title)) return "Onsite";
  return "Online";
};

export const isOptionalSession = (session?: Session | null): boolean =>
  String(session?.live_session_category || "").trim().toLowerCase() === "optional";

export const isWorkshopSession = (session?: Session | null): boolean => {
  const category = String(session?.live_session_category || "")
    .trim()
    .toLowerCase();
  if (
    category === "workshop_online" ||
    category === "workshop_onsite" ||
    category === "workshop"
  ) {
    return true;
  }
  const title = String(session?.Session_Topic || "").toUpperCase();
  if (
    title.startsWith("LW-ONLINE") ||
    title.startsWith("LW-ONSITE") ||
    /^LW\s/.test(title)
  ) {
    return true;
  }
  return Array.isArray(session?.day_schedules) && session!.day_schedules!.length > 0;
};

function calendarDayCount(startDate: string, endDate: string): number {
  const s = String(startDate || "").trim().slice(0, 10);
  const e = String(endDate || startDate || "").trim().slice(0, 10);
  if (!s) return 1;
  const start = new Date(`${s}T00:00:00`);
  const end = new Date(`${e}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 1;
  const diff = Math.round((end.getTime() - start.getTime()) / 86400000);
  return Math.max(1, diff + 1);
}

function addDaysToDateStr(dateStr: string, days: number): string {
  const s = String(dateStr || "").trim().slice(0, 10);
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const isDayFinished = (day?: WorkshopDayScheduleRow | null): boolean => {
  if (!day) return false;
  if (day.has_ended) return true;
  const status = String(day.status || "").trim().toLowerCase();
  return status === "ended" || status === "completed" || status === "cancelled";
};

/** True when the LMS session itself is fully finished (not a mid-workshop day end). */
export const isSessionFullyFinished = (session: Session): boolean => {
  const status = String(session.session_status || "").trim().toLowerCase();
  if (status === "ended" || status === "completed" || status === "cancelled") {
    return true;
  }
  if (!isWorkshopSession(session)) {
    return Boolean(String(session.End_Time_time || "").trim());
  }
  const days = Array.isArray(session.day_schedules) ? session.day_schedules : [];
  if (days.length > 0 && days.every(isDayFinished)) return true;
  // Prefer explicit completed flag — hasEnded is also true for partial workshops historically.
  return false;
};

/** Drop finished workshops/sessions from an Ongoing bucket payload. */
export const filterOngoingSessions = (sessions: Session[]): Session[] =>
  sessions.filter((s) => !isSessionFullyFinished(s));

/** Day 0 may start anytime; later days require the previous day to be finished. */
export const isPreviousWorkshopDayCompleted = (
  days: WorkshopDayScheduleRow[],
  dayIndex: number,
): boolean => {
  const idx = Number(dayIndex);
  if (!Number.isFinite(idx) || idx <= 0) return true;
  const prev = days.find((d) => Number(d.day_index) === idx - 1);
  return isDayFinished(prev);
};

/** Resolve workshop day rows from API or synthesize from schedule + topic_plan. */
export const resolveWorkshopDays = (session: Session): WorkshopDayScheduleRow[] => {
  const existing = Array.isArray(session.day_schedules) ? session.day_schedules : [];
  if (!isWorkshopSession(session)) return existing;

  const startDate = String(session.Date || "").trim();
  const endDate = String(
    session.Expected_End_Time_date || session.End_Time_date || session.Date || "",
  ).trim();
  let dayCount = calendarDayCount(startDate, endDate);

  const plan = session.topic_plan || [];
  let maxTopicDay = -1;
  let totalMinutes = 0;
  for (const t of plan) {
    const raw = t.workshop_day_index ?? t.workshopDayIndex;
    let dayIdx: number | null =
      raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : null;
    if (dayIdx == null) {
      const text = `${t.description || ""} ${t.title || ""}`;
      const match = /\bday\s*(\d+)\b/i.exec(String(text));
      if (match) dayIdx = Math.max(0, Number(match[1]) - 1);
    }
    if (dayIdx != null && !Number.isNaN(dayIdx)) {
      maxTopicDay = Math.max(maxTopicDay, dayIdx);
    }
    const dur = Number(t.duration_minutes ?? t.durationMinutes ?? 0);
    if (dur > 0) totalMinutes += dur;
  }
  if (maxTopicDay >= 0) dayCount = Math.max(dayCount, maxTopicDay + 1);
  if (totalMinutes > 0 && existing.length === 0) {
    dayCount = Math.max(dayCount, Math.ceil(totalMinutes / 480));
  }
  const storedCount = Number(session.workshop_day_count || 0);
  if (storedCount > 0) dayCount = Math.max(dayCount, storedCount);

  // When day_schedules already exist, do not invent extra empty days from calendar span
  if (existing.length > 0) {
    const fromTopics = maxTopicDay >= 0 ? maxTopicDay + 1 : 0;
    dayCount = Math.max(
      existing.length,
      fromTopics,
      storedCount > 0 ? storedCount : 0,
      1,
    );
  } else {
    dayCount = Math.max(dayCount, 1);
  }

  if (existing.length >= dayCount && existing.length > 0) {
    const sessionFinished = isSessionFullyFinished(session);
    const sliced = existing.slice(0, dayCount).map((d, i) => {
      let status = String(d.status || "scheduled").toLowerCase();
      if (sessionFinished && status === "live") status = "ended";
      return {
        ...d,
        status,
        can_join: status === "live",
        has_ended:
          Boolean(d.has_ended) ||
          status === "ended" ||
          status === "completed" ||
          status === "cancelled",
        topics: d.topics?.length ? d.topics : byDayTopics(plan, i),
      };
    });
    return sliced.map((d) => {
      const status = String(d.status || "scheduled").toLowerCase();
      const canStartBySequence = isPreviousWorkshopDayCompleted(
        sliced,
        Number(d.day_index),
      );
      return {
        ...d,
        can_start:
          d.can_start === false
            ? false
            : status === "scheduled" && canStartBySequence,
      };
    });
  }

  const byDay = new Map<number, string[]>();
  for (const t of plan) {
    const raw = t.workshop_day_index ?? t.workshopDayIndex;
    let idx =
      raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : null;
    if (idx == null) {
      const text = `${t.description || ""} ${t.title || ""}`;
      const match = /\bday\s*(\d+)\b/i.exec(String(text));
      if (match) idx = Math.max(0, Number(match[1]) - 1);
    }
    if (idx == null) idx = 0;
    const title = String(t.title || t.name || "").trim();
    if (!title) continue;
    const list = byDay.get(idx) || [];
    list.push(title);
    byDay.set(idx, list);
  }

  const startTime = String(session.Start_Time || "").trim();
  const sessionFinished = isSessionFullyFinished(session);
  const isLive =
    !sessionFinished &&
    (String(session.session_status || "").toLowerCase() === "live" ||
      Boolean(session.Actual_Start_Time_time));

  const days: WorkshopDayScheduleRow[] = [];
  for (let i = 0; i < dayCount; i += 1) {
    const persisted = existing.find((d) => Number(d.day_index) === i);
    if (persisted) {
      const status = sessionFinished
        ? isDayFinished(persisted)
          ? String(persisted.status || "ended").toLowerCase()
          : "ended"
        : String(persisted.status || "scheduled").toLowerCase();
      days.push({
        ...persisted,
        status,
        can_join: status === "live",
        has_ended: status === "ended" || status === "completed" || status === "cancelled",
        topics: persisted.topics?.length ? persisted.topics : byDay.get(i) || [],
      });
      continue;
    }
    const status = sessionFinished
      ? "ended"
      : isLive && i === 0
        ? "live"
        : "scheduled";
    days.push({
      day_index: i,
      day_label: `Day ${i + 1}`,
      start_date: startDate ? addDaysToDateStr(startDate, i) : startDate,
      start_time: startTime,
      status,
      can_start: status === "scheduled",
      can_join: status === "live",
      has_ended: status === "ended",
      topics: byDay.get(i) || [],
    });
  }
  return days.map((d) => {
    const status = String(d.status || "scheduled").toLowerCase();
    return {
      ...d,
      can_start:
        status === "scheduled" &&
        isPreviousWorkshopDayCompleted(days, Number(d.day_index)),
    };
  });
};

function byDayTopics(plan: Array<Record<string, unknown>>, dayIndex: number): string[] {
  return plan
    .filter((t) => {
      const raw = t.workshop_day_index ?? t.workshopDayIndex;
      const idx = raw != null && !Number.isNaN(Number(raw)) ? Number(raw) : 0;
      return idx === dayIndex;
    })
    .map((t) => String(t.title || t.name || "").trim())
    .filter(Boolean);
}

export const asIdList = (value: string | string[] | undefined | null): string[] => {
  if (Array.isArray(value)) {
    return value.map((id) => String(id || "").trim()).filter(Boolean);
  }
  const single = String(value || "").trim();
  return single ? [single] : [];
};

const formatDateTimeLabel = (date: string, timeRaw: string): string => {
  const d = String(date || "").trim();
  const t = String(timeRaw || "").trim();
  if (!d) return "—";
  if (!t) return d;
  const already12h = /AM|PM/i.test(t);
  const time = already12h ? t : convertTo12HourFormat(t);
  return `${d} · ${time.replace(/^0/, "")}`;
};

/** Scheduled start, e.g. 21-07-2026 · 3:43 PM */
export const formatSessionStartLabel = (session: Session): string =>
  formatDateTimeLabel(session.Date, session.Start_Time);

export const formatWorkshopDayStartLabel = (day: {
  start_date?: string;
  start_time?: string;
  day_label?: string;
}): string => {
  const when = formatDateTimeLabel(day.start_date || "", day.start_time || "");
  const label = String(day.day_label || "").trim();
  return label ? `${label}: ${when}` : when;
};

/** Prefer actual start when present (completed rows). */
export const formatSessionActualStartLabel = (session: Session): string => {
  const actualDate = String(session.Actual_Start_Time_date || "").trim();
  const actualTime = String(session.Actual_Start_Time_time || "").trim();
  if (actualDate) {
    return formatDateTimeLabel(actualDate, actualTime || session.Start_Time);
  }
  return formatSessionStartLabel(session);
};

/** Expected or actual end for listing tables. */
export const formatSessionEndLabel = (session: Session): string => {
  const endDate =
    String(session.End_Time_date || "").trim() ||
    String(session.Expected_End_Time_date || "").trim();
  const endTime =
    String(session.End_Time_time || "").trim() ||
    String(session.Expected_End_Time_time || "").trim();
  return formatDateTimeLabel(endDate, endTime);
};

export const getSessionAttendancePercent = (session: Session): string => {
  // Prefer headcount (matches Session Report Overview 1/N badge).
  const invited = Number(session.attendances?.invited_students_count);
  const attended = Number(session.attendances?.attended_students_count);
  if (Number.isFinite(invited) && invited > 0 && Number.isFinite(attended) && attended >= 0) {
    return `${Math.round((attended * 100) / invited)}%`;
  }
  const values = session.attendances_time?.split(" / ");
  if (values && values.length === 2) {
    const attendedTime = (Number(values[0]) * 100) / Number(values[1]);
    if (Number.isFinite(attendedTime)) {
      return `${Math.round(attendedTime)}%`;
    }
  }
  return "—";
};

